var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var FuzzyMatcherConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ColumnFormat, CustomFormat, DefaultSettings } from './customFormat';
import './fuzzyMatcherMatchColumns'; // force import for JsonSchema
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
// only edit-distance in v0
export var FuzzyMatcherType;
(function (FuzzyMatcherType) {
    FuzzyMatcherType["EDIT_DISTANCE"] = "edit-distance";
})(FuzzyMatcherType || (FuzzyMatcherType = {}));
// fuzzyMatcherSource identifies which dataset is the key/source
export var fuzzyMatcherSource;
(function (fuzzyMatcherSource) {
    fuzzyMatcherSource["top"] = "top";
    fuzzyMatcherSource["bottom"] = "bottom";
})(fuzzyMatcherSource || (fuzzyMatcherSource = {}));
export var FuzzyMatcherDefaultColumnName;
(function (FuzzyMatcherDefaultColumnName) {
    FuzzyMatcherDefaultColumnName["similarity_measure"] = "Similarity Measure";
})(FuzzyMatcherDefaultColumnName || (FuzzyMatcherDefaultColumnName = {}));
const schema = {
    $id: 'urn:wisepipe-common-pipe:FuzzyMatcherConfig',
    type: 'object',
    properties: {
        fuzzyMatcherType: {
            type: 'string',
            enum: Object.values(FuzzyMatcherType)
        },
        matchColumns: {
            type: 'array',
            items: { $ref: 'urn:wisepipe-common-pipe:FuzzyMatcherMatchColumns' },
            minItems: 1,
            maxItems: 20,
            uniqueItemProperties: ['dataColumn', 'sourceColumn']
        },
        fuzzyMatcherSource: {
            type: 'string',
            enum: Object.values(fuzzyMatcherSource)
        },
        similarityColumnName: { type: 'string' }
    },
    required: ['fuzzyMatcherType', 'matchColumns', 'fuzzyMatcherSource', 'similarityColumnName']
};
let FuzzyMatcherConfig = FuzzyMatcherConfig_1 = class FuzzyMatcherConfig extends ToolConfig {
    constructor(fuzzyMatcherType, matchColumns, fuzzyMatcherSource, similarityColumnName = FuzzyMatcherDefaultColumnName.similarity_measure, customFormats) {
        super(ToolType.FUZZY_MATCHER, customFormats);
        this.fuzzyMatcherType = fuzzyMatcherType;
        this.matchColumns = matchColumns;
        this.fuzzyMatcherSource = fuzzyMatcherSource;
        this.similarityColumnName = similarityColumnName || FuzzyMatcherDefaultColumnName.similarity_measure;
        JSON.validate(this, FuzzyMatcherConfig_1);
    }
    get source() {
        if (this.fuzzyMatcherSource === fuzzyMatcherSource.top) {
            return FuzzyMatcherConfig_1.top;
        }
        return FuzzyMatcherConfig_1.bottom;
    }
    get data() {
        if (this.fuzzyMatcherSource === fuzzyMatcherSource.top) {
            return FuzzyMatcherConfig_1.bottom;
        }
        return FuzzyMatcherConfig_1.top;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return FuzzyMatcherConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return FuzzyMatcherConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return FuzzyMatcherConfig_1.outs;
    }
    static get top() {
        return FuzzyMatcherConfig_1.ins[0];
    }
    static get bottom() {
        return FuzzyMatcherConfig_1.ins[1];
    }
    static get out() {
        return FuzzyMatcherConfig_1.outs[0];
    }
    static get ins() {
        return ['top', 'bottom'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.FUZZY_MATCHER.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.COMBINE], this.ins, this.outs, ['match', 'fuzzy', 'merge', 'combine'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const data = ins.schemas.at(this.data);
        const source = ins.schemas.at(this.source);
        const dataJoinCols = new Set();
        const sourceJoinCols = new Set();
        this.matchColumns.forEach((join) => {
            dataJoinCols.add(join.dataColumn);
            sourceJoinCols.add(join.sourceColumn);
        });
        // generate the dependents, dependents would be the fuzzy match join columns on the data/source sides
        const dependents = [];
        data.forEach((colMet) => {
            if (dataJoinCols.has(colMet.name))
                dependents.push(colMet);
        });
        source.forEach((colMet) => {
            if (sourceJoinCols.has(colMet.name))
                dependents.push(colMet);
        });
        const dataColumns = data.map((l) => l.name);
        const sourceColumns = source.map((r) => r.name);
        const aliases = ToolConfig.computeAliases(dataColumns, sourceColumns);
        data.forEach((c) => {
            if (dataJoinCols.has(c.name))
                exp.copy(this.data, c, false, true, dependents);
            else
                exp.copy(this.data, c, false, false);
        });
        source.forEach((c) => {
            if (sourceJoinCols.has(c.name))
                exp.copy(this.source, c, false, true, dependents, aliases.get(c.name));
            else
                exp.copy(this.source, c, false, false, undefined, aliases.get(c.name));
        });
        const newColumnFormats = new Map();
        newColumnFormats.set(this.similarityColumnName, new CustomFormat(ColumnFormat.Percentage, 0, DefaultSettings.thousandSeparator, true));
        exp
            .makeNew(new Column(this.similarityColumnName, 'number'), dependents, true)
            .initializeColumnFormats()
            .setColumnFormats(newColumnFormats)
            .setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const outputs = [this.similarityColumnName];
        const data = scope.schemas.at(this.data);
        const source = scope.schemas.at(this.source);
        if (!data || !source) {
            return ToolValidationState.PORT_MISSING_EDGES;
        }
        const dataColumnPool = data.reduce((set, c) => set.add(c.name), new Set());
        const sourceColumnPool = source.reduce((set, c) => set.add(c.name), new Set());
        const dataColumns = data.map((l) => l.name);
        const sourceColumns = source.map((r) => r.name);
        const aliases = ToolConfig.computeAliases(dataColumns, sourceColumns);
        const aliasesValues = new Set(aliases.values());
        // ensure data doesn't have name conflict with output columns
        for (const output of outputs) {
            if (dataColumnPool.has(output))
                return ToolValidationState.BAD_METADATA;
            if (sourceColumnPool.has(output) && !aliases.has(output))
                return ToolValidationState.BAD_METADATA;
            if (aliasesValues.has(output))
                return ToolValidationState.BAD_METADATA;
        }
        // check if the inputs contain the columns used for matching
        for (const cols of this.matchColumns) {
            if (!dataColumnPool.has(cols.dataColumn) || !sourceColumnPool.has(cols.sourceColumn)) {
                return ToolValidationState.BAD_METADATA;
            }
        }
        return ToolValidationState.VALID;
    }
};
FuzzyMatcherConfig = FuzzyMatcherConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.FUZZY_MATCHER)
], FuzzyMatcherConfig);
export { FuzzyMatcherConfig };
//# sourceMappingURL=fuzzyMatcherConfig.js.map